import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SLOT_LABEL, teamName, teamColor, teamSpread, favoriteTeam } from "@/lib/teams";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Flag, Users, UserPlus, ShieldAlert, RefreshCw, Sliders, Link2, Copy, KeyRound, Pencil, LayoutDashboard, CheckCircle2, Clock, Lock, AlertCircle, Trophy } from "lucide-react";

const SLOTS = [
  { value: "thu_mon", label: "Thu/Mon/Standalone", pts: 1.5 },
  { value: "sun_early", label: "Sunday Day", pts: 2.5 },
  { value: "snf", label: "Sunday Night", pts: 5.0 },
];
const slotPts = (s) => (s === "thu_mon" ? 1.5 : s === "snf" ? 5.0 : 2.5);

function toLocalInput(iso) {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [weeks, setWeeks] = useState([]);

  const loadWeeks = () => api.get("/weeks").then((res) => setWeeks(res.data));
  useEffect(() => { loadWeeks(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="font-['Bebas_Neue'] text-5xl tracking-tight leading-none mb-1">COMMISSIONER</h1>
      <p className="text-white/50 text-sm mb-6">Set matchups & points, mark or sync winners, tune bonuses, manage players.</p>

      <div className="flex gap-1 mb-6 border border-white/10 w-fit flex-wrap">
        {[["dashboard", "Dashboard"], ["winners", "Scores & Winners"], ["edit", "Edit Week"], ["editpicks", "Edit Picks"], ["scoring", "Scoring Rules"], ["players", "Manage Players"]].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-200 ${
              tab === v ? "bg-[#E31837] text-white" : "text-white/50 hover:text-white"
            }`}
            data-testid={`admin-tab-${v}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <Dashboard />}
      {tab === "winners" && <Winners weeks={weeks} />}
      {tab === "edit" && <EditWeek weeks={weeks} onSaved={loadWeeks} />}
      {tab === "editpicks" && <EditPicks weeks={weeks} />}
      {tab === "scoring" && <ScoringRules />}
      {tab === "players" && <Players />}
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-white/50 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading dashboard…</div>;
  if (!data || data.week_number == null) {
    return <div className="bg-[#141414] border border-white/10 px-6 py-16 text-center text-white/50" data-testid="dashboard-empty">No active week — the season is complete.</div>;
  }

  const cutoff = new Date(data.cutoff);
  const cutoffStr = cutoff.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const picked = data.players.filter((p) => p.picked);
  const missing = data.players.filter((p) => !p.picked);
  const pct = data.total_players ? Math.round((data.picked_count / data.total_players) * 100) : 0;

  const copyReminder = async () => {
    const names = missing.map((p) => p.username).join(", ");
    const msg = `🏈 Pick'em reminder — Week ${data.week_number}\nPicks lock ${cutoffStr}.\nStill waiting on: ${names}.\nGet 'em in before kickoff!`;
    try {
      await navigator.clipboard.writeText(msg);
      toast.success("Reminder copied — paste it in your group chat!");
    } catch {
      toast.message("Copy this reminder", { description: msg });
    }
  };

  return (
    <div data-testid="dashboard-panel">
      {data.last_finalized && data.last_finalized.winners.length > 0 && (
        <div className="relative overflow-hidden border border-[#EAB308]/50 bg-[#12100a] px-5 py-4 mb-6 flex items-center gap-4" data-testid="week-winner-spotlight">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 0% 50%, rgba(234,179,8,0.18), transparent 55%)" }} />
          <Trophy className="w-8 h-8 text-[#EAB308] shrink-0 relative" />
          <div className="relative flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#EAB308] mb-1">
              Week {data.last_finalized.week_number} {data.last_finalized.is_tie ? "Co-Winners" : "Winner"}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {data.last_finalized.winners.map((w) => (
                <span key={w.username} className="flex items-center gap-2" data-testid={`week-winner-${w.username}`}>
                  <Avatar team={w.avatar} username={w.username} size="sm" />
                  <span className="font-['Bebas_Neue'] text-2xl tracking-wide leading-none">{w.username}</span>
                </span>
              ))}
              <span className="font-['Bebas_Neue'] text-2xl tracking-wide text-[#E31837] leading-none">{data.last_finalized.points} PTS</span>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <div className="bg-[#141414] border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-2"><LayoutDashboard className="w-4 h-4 text-[#E31837]" /><span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Active Week</span></div>
          <div className="font-['Bebas_Neue'] text-4xl tracking-wide leading-none text-white">Week {data.week_number}</div>
          <div className="text-xs text-white/40 mt-1">{data.games_count} games · {data.decided} decided</div>
        </div>
        <div className="bg-[#141414] border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-2">{data.locked ? <Lock className="w-4 h-4 text-[#EF4444]" /> : <Clock className="w-4 h-4 text-[#EAB308]" />}<span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Pick Cutoff</span></div>
          <div className={`font-['Bebas_Neue'] text-2xl tracking-wide leading-tight ${data.locked ? "text-[#EF4444]" : "text-[#EAB308]"}`}>{data.locked ? "LOCKED" : "OPEN"}</div>
          <div className="text-xs text-white/40 mt-1">{cutoffStr}</div>
        </div>
        <div className="bg-[#141414] border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /><span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Picks In</span></div>
          <div className="font-['Bebas_Neue'] text-4xl tracking-wide leading-none text-[#22C55E]" data-testid="dashboard-picked-count">{data.picked_count}<span className="text-white/30 text-2xl">/{data.total_players}</span></div>
          <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#22C55E] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="bg-[#141414] border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-2"><AlertCircle className="w-4 h-4 text-[#EAB308]" /><span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Still Missing</span></div>
          <div className="font-['Bebas_Neue'] text-4xl tracking-wide leading-none text-[#EAB308]" data-testid="dashboard-missing-count">{missing.length}</div>
          <div className="text-xs text-white/40 mt-1">{missing.length === 0 ? "Everyone's in!" : "Yet to submit"}</div>
        </div>
      </div>

      {data.total_players === 0 ? (
        <div className="bg-[#141414] border border-white/10 px-6 py-12 text-center text-white/50">No players in the pool yet. Share your invite link from Manage Players.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide mb-3 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-[#EAB308]" /> Waiting On ({missing.length})</span>
              {missing.length > 0 && (
                <button onClick={copyReminder} className="flex items-center gap-1.5 bg-[#EAB308] hover:bg-[#CA9A04] text-black px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-[0.2em] font-bold transition-colors duration-200" data-testid="copy-reminder-button">
                  <Copy className="w-3.5 h-3.5" /> Copy Reminder
                </button>
              )}
            </h3>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="dashboard-missing-list">
              {missing.length === 0 ? (
                <div className="px-4 py-6 text-center text-white/40 text-sm">Every player has locked in their picks.</div>
              ) : missing.map((p) => (
                <div key={p.username} className="flex items-center gap-3 px-4 py-2.5" data-testid={`dashboard-missing-${p.username}`}>
                  <Avatar team={p.avatar} username={p.username} size="sm" />
                  <span className="flex-1">{p.username}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#EAB308]">No pick</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide mb-3 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#22C55E]" /> Locked In ({picked.length})</h3>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="dashboard-picked-list">
              {picked.length === 0 ? (
                <div className="px-4 py-6 text-center text-white/40 text-sm">No picks submitted yet.</div>
              ) : picked.map((p) => (
                <div key={p.username} className="flex items-center gap-3 px-4 py-2.5" data-testid={`dashboard-picked-${p.username}`}>
                  <Avatar team={p.avatar} username={p.username} size="sm" />
                  <span className="flex-1">{p.username}</span>
                  <CheckCircle2 className="w-4 h-4 text-[#22C55E]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Winners({ weeks }) {
  const [weekNum, setWeekNum] = useState(null);
  const [week, setWeek] = useState(null);
  const [winners, setWinners] = useState({});
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (weeks.length && weekNum == null) setWeekNum(weeks[0].week_number);
  }, [weeks, weekNum]);

  const loadWeek = () => {
    if (!weekNum) return;
    api.get(`/weeks/${weekNum}`).then((res) => {
      setWeek(res.data);
      const w = {}, s = {};
      res.data.games.forEach((g) => {
        w[g.id] = g.winner || "";
        s[g.id] = { away: g.away_score ?? "", home: g.home_score ?? "" };
      });
      setWinners(w); setScores(s);
    });
  };
  useEffect(loadWeek, [weekNum]);

  const setScore = (gid, side, val, g) => {
    const next = { ...scores, [gid]: { ...scores[gid], [side]: val } };
    setScores(next);
    const a = parseInt(next[gid].away, 10);
    const h = parseInt(next[gid].home, 10);
    if (!isNaN(a) && !isNaN(h) && a !== h) {
      setWinners((w) => ({ ...w, [gid]: a > h ? g.away : g.home }));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const games = {};
      week.games.forEach((g) => {
        games[g.id] = {
          winner: winners[g.id] || "",
          away_score: scores[g.id]?.away === "" ? null : parseInt(scores[g.id].away, 10),
          home_score: scores[g.id]?.home === "" ? null : parseInt(scores[g.id].home, 10),
        };
      });
      await api.post(`/admin/weeks/${weekNum}/winners`, { games });
      toast.success("Saved!", { description: "Scores & winners updated." });
      loadWeek();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const syncEspn = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/admin/weeks/${weekNum}/sync`);
      toast.success("Synced from ESPN", { description: `${res.data.updated} games updated.` });
      loadWeek();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  if (!week) return <div className="text-white/50 py-10">Select a week…</div>;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={weekNum || ""}
          onChange={(e) => setWeekNum(Number(e.target.value))}
          className="appearance-none bg-[#141414] border border-white/10 px-4 py-2.5 rounded-sm text-sm uppercase tracking-wider focus:ring-2 focus:ring-[#E31837] focus:outline-none"
          data-testid="winners-week-selector"
        >
          {weeks.map((w) => <option key={w.week_number} value={w.week_number}>Week {w.week_number}</option>)}
        </select>
        <button onClick={syncEspn} disabled={syncing}
          className="flex items-center gap-2 border border-white/20 hover:border-white/40 px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest transition-colors duration-200 disabled:opacity-60"
          data-testid="sync-espn-button">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync From ESPN
        </button>
        <span className="text-xs text-white/40">Live scores auto-sync during game days. Or enter manually below.</span>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {week.games.map((g) => (
          <div key={g.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`winner-row-${g.id}`}>
            <span className="text-[10px] uppercase tracking-widest text-white/40 w-28 shrink-0">{SLOT_LABEL[g.slot]}</span>
            <span className="text-sm w-28 shrink-0">{g.away} @ {g.home}</span>
            <span className="text-xs text-[#E31837] w-10 shrink-0">{g.points}pt</span>
            <div className="flex items-center gap-1 shrink-0">
              <input type="number" value={scores[g.id]?.away ?? ""} onChange={(e) => setScore(g.id, "away", e.target.value, g)}
                placeholder={g.away} className="w-14 bg-[#0A0A0A] border border-white/10 px-2 py-1.5 rounded-sm text-center text-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid={`score-away-${g.id}`} />
              <span className="text-white/30">-</span>
              <input type="number" value={scores[g.id]?.home ?? ""} onChange={(e) => setScore(g.id, "home", e.target.value, g)}
                placeholder={g.home} className="w-14 bg-[#0A0A0A] border border-white/10 px-2 py-1.5 rounded-sm text-center text-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid={`score-home-${g.id}`} />
            </div>
            <div className="flex gap-1 ml-auto">
              {[g.away, g.home].map((t) => (
                <button key={t}
                  onClick={() => setWinners((w) => ({ ...w, [g.id]: w[g.id] === t ? "" : t }))}
                  className={`px-3 py-1.5 text-xs font-bold border transition-colors duration-200 ${
                    winners[g.id] === t ? "border-[#22C55E] bg-[#22C55E]/15 text-[#22C55E]" : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`set-winner-${g.id}-${t}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving}
        className="mt-4 bg-[#E31837] hover:bg-[#C8102E] px-6 py-3 rounded-sm uppercase tracking-[0.2em] text-sm font-semibold transition-colors duration-200 flex items-center gap-2 disabled:opacity-60"
        data-testid="save-winners-button">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} Save Scores & Winners
      </button>
    </div>
  );
}

function EditWeek({ weeks, onSaved }) {
  const [weekNum, setWeekNum] = useState(null);
  const [cutoff, setCutoff] = useState(toLocalInput());
  const [rows, setRows] = useState([{ id: undefined, away: "", home: "", slot: "sun_early", points: 2.5, spread: "" }]);
  const [saving, setSaving] = useState(false);

  const loadExisting = (wn) => {
    const exists = weeks.find((w) => w.week_number === wn);
    if (exists) {
      api.get(`/weeks/${wn}`).then((res) => {
        setCutoff(toLocalInput(res.data.cutoff));
        setRows(res.data.games.map((g) => ({ id: g.id, away: g.away, home: g.home, slot: g.slot, points: g.points, spread: g.spread ?? "" })));
      });
    } else {
      setRows([{ id: undefined, away: "", home: "", slot: "sun_early", points: 2.5, spread: "" }]);
      setCutoff(toLocalInput());
    }
  };

  // Load the first existing week by default so the tab opens in "edit" mode.
  useEffect(() => {
    if (weeks.length && weekNum == null) {
      const first = weeks[0].week_number;
      setWeekNum(first);
      loadExisting(first);
    }
  }, [weeks]); // eslint-disable-line

  const update = (i, key, val) => setRows((r) => r.map((row, idx) => {
    if (idx !== i) return row;
    const next = { ...row, [key]: val };
    if (key === "slot") next.points = slotPts(val);
    return next;
  }));
  const addRow = () => setRows((r) => [...r, { id: undefined, away: "", home: "", slot: "sun_early", points: 2.5, spread: "" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    const games = rows
      .filter((r) => r.away.trim() && r.home.trim())
      .map((r) => {
        const g = { away: r.away.trim().toUpperCase(), home: r.home.trim().toUpperCase(), slot: r.slot, points: parseFloat(r.points) };
        const sp = r.spread === "" || r.spread === null || r.spread === undefined ? null : parseFloat(r.spread);
        g.spread = Number.isNaN(sp) ? null : sp;
        if (r.id) g.id = r.id;
        return g;
      });
    if (!games.length) return toast.error("Add at least one matchup.");
    setSaving(true);
    try {
      await api.post("/admin/weeks", { week_number: Number(weekNum), cutoff: new Date(cutoff).toISOString(), games });
      toast.success(`Week ${weekNum} saved!`, { description: `${games.length} matchups live.` });
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteWeek = async () => {
    if (!window.confirm(`Delete Week ${weekNum} and all its picks? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/weeks/${Number(weekNum)}`);
      toast.success(`Week ${weekNum} deleted.`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exists = weeks.some((w) => w.week_number === Number(weekNum));

  return (
    <div>
      <div className="flex flex-wrap gap-4 mb-5 items-end">
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-1">Select Week</label>
          <select
            value={weekNum ?? ""}
            onChange={(e) => { const v = Number(e.target.value); setWeekNum(v); loadExisting(v); }}
            className="appearance-none bg-[#141414] border border-white/10 px-4 py-2.5 rounded-sm text-sm uppercase tracking-wider focus:ring-2 focus:ring-[#E31837] focus:outline-none"
            data-testid="edit-week-selector"
          >
            {weeks.map((w) => <option key={w.week_number} value={w.week_number}>Week {w.week_number}</option>)}
            {weekNum != null && !exists && <option value={weekNum}>Week {weekNum} (new)</option>}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-1">Or New Week #</label>
          <input
            type="number"
            value={weekNum ?? ""}
            onChange={(e) => { setWeekNum(e.target.value); loadExisting(Number(e.target.value)); }}
            className="bg-[#141414] border border-white/10 px-4 py-2.5 rounded-sm w-24 focus:ring-2 focus:ring-[#E31837] focus:outline-none"
            data-testid="build-week-number"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-1">Games Start / Picks Lock</label>
          <input
            type="datetime-local"
            value={cutoff}
            onChange={(e) => setCutoff(e.target.value)}
            className="bg-[#141414] border border-white/10 px-4 py-2.5 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none [color-scheme:dark]"
            data-testid="build-cutoff"
          />
        </div>
      </div>
      <p className="text-xs text-white/40 mb-4">Add or remove matchups, change point values, and set the kickoff / pick-lock time. Editing an existing week keeps any scores and winners already recorded.</p>

      <div className="border border-white/10 divide-y divide-white/10 mb-4">
        <div className="grid grid-cols-[1fr_1fr_1.2fr_80px_90px_auto] gap-2 px-3 py-2 bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-white/40">
          <span>Away</span><span>Home</span><span>Slot</span><span>Points</span><span>Spread</span><span></span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1.2fr_80px_90px_auto] gap-2 px-3 py-2 items-center" data-testid={`build-row-${i}`}>
            <input value={r.away} onChange={(e) => update(i, "away", e.target.value)} placeholder="KC"
              className="bg-[#141414] border border-white/10 px-3 py-2 rounded-sm uppercase focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid={`build-away-${i}`} />
            <input value={r.home} onChange={(e) => update(i, "home", e.target.value)} placeholder="BAL"
              className="bg-[#141414] border border-white/10 px-3 py-2 rounded-sm uppercase focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid={`build-home-${i}`} />
            <select value={r.slot} onChange={(e) => update(i, "slot", e.target.value)}
              className="bg-[#141414] border border-white/10 px-3 py-2 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid={`build-slot-${i}`}>
              {SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <input type="number" step="0.5" value={r.points} onChange={(e) => update(i, "points", e.target.value)}
              className="bg-[#141414] border border-white/10 px-2 py-2 rounded-sm text-center focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid={`build-points-${i}`} />
            <input type="number" step="0.5" value={r.spread} onChange={(e) => update(i, "spread", e.target.value)} placeholder="home"
              title="Home team's line (negative = home favored). Leave blank for no line / pick'em."
              className="bg-[#141414] border border-white/10 px-2 py-2 rounded-sm text-center focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid={`build-spread-${i}`} />
            <button onClick={() => removeRow(i)} className="text-white/40 hover:text-[#EF4444] transition-colors duration-200 p-2" data-testid={`build-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3 flex-wrap">
        <button onClick={addRow} className="flex items-center gap-2 border border-white/20 hover:border-white/40 px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest transition-colors duration-200" data-testid="build-add-row">
          <Plus className="w-4 h-4" /> Add Matchup
        </button>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#E31837] hover:bg-[#C8102E] px-6 py-2.5 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-200 disabled:opacity-60" data-testid="save-week-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
        </button>
        {exists && (
          <button onClick={deleteWeek} className="flex items-center gap-2 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest transition-colors duration-200 ml-auto" data-testid="delete-week-button">
            <Trash2 className="w-4 h-4" /> Delete Week
          </button>
        )}
      </div>
    </div>
  );
}

function EditPicks({ weeks }) {
  const [weekNum, setWeekNum] = useState(null);
  const [players, setPlayers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [userId, setUserId] = useState("");
  const [data, setData] = useState(null);
  const [selections, setSelections] = useState({});
  const [spreadPicks, setSpreadPicks] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);
  useEffect(() => {
    if (weeks.length && weekNum == null) setWeekNum(weeks[0].week_number);
  }, [weeks, weekNum]);

  const load = () => {
    if (!weekNum || !userId) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/picks/${weekNum}/${userId}`)
      .then((r) => {
        setData(r.data);
        setSelections(r.data.picks?.selections || {});
        setSpreadPicks(new Set(r.data.picks?.spread_picks || []));
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [weekNum, userId]); // eslint-disable-line

  const setPick = (gid, team) => {
    setSelections((s) => ({ ...s, [gid]: s[gid] === team ? undefined : team }));
    // if unpicked or switched off the favorite, drop the spread flag
    setSpreadPicks((sp) => {
      const next = new Set(sp);
      next.delete(gid);
      return next;
    });
  };
  const clearPick = (gid) => {
    setSelections((s) => { const n = { ...s }; delete n[gid]; return n; });
    setSpreadPicks((sp) => { const n = new Set(sp); n.delete(gid); return n; });
  };
  const toggleSpread = (gid, on) => {
    setSpreadPicks((sp) => {
      const n = new Set(sp);
      if (on) n.add(gid); else n.delete(gid);
      return n;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      Object.entries(selections).forEach(([k, v]) => { if (v) cleaned[k] = v; });
      await api.post(`/admin/picks/${weekNum}/${userId}`, {
        selections: cleaned,
        spread_picks: Array.from(spreadPicks),
      });
      toast.success(`Picks saved for ${data.user.username}`, { description: `Week ${weekNum}` });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const mode = settings?.pick_mode || "straight_up";
  const showSpreadUI = mode === "ats" || mode === "combo";
  const week = data?.week;

  return (
    <div data-testid="editpicks-panel">
      <div className="bg-[#141414] border border-[#EAB308]/40 px-4 py-3 mb-5 flex items-start gap-3">
        <Lock className="w-4 h-4 text-[#EAB308] mt-0.5 shrink-0" />
        <div className="text-xs text-white/70">
          <span className="text-[#EAB308] font-semibold uppercase tracking-widest text-[10px]">Commissioner override</span>
          <div className="mt-0.5">Edit any player's picks — even after the week is locked. Saved picks are recorded with your name on the audit trail.</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-end mb-5">
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-1">Week</label>
          <select value={weekNum || ""} onChange={(e) => setWeekNum(Number(e.target.value))}
            className="appearance-none bg-[#141414] border border-white/10 px-4 py-2.5 rounded-sm text-sm uppercase tracking-wider focus:ring-2 focus:ring-[#E31837] focus:outline-none"
            data-testid="editpicks-week-selector">
            {weeks.map((w) => <option key={w.week_number} value={w.week_number}>Week {w.week_number}{w.locked ? " · LOCKED" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-1">Player</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}
            className="appearance-none bg-[#141414] border border-white/10 px-4 py-2.5 rounded-sm text-sm min-w-[220px] focus:ring-2 focus:ring-[#E31837] focus:outline-none"
            data-testid="editpicks-player-selector">
            <option value="">— Select player —</option>
            {players.map((p) => <option key={p.id} value={p.id}>{p.username}</option>)}
          </select>
        </div>
        {data && (
          <button onClick={save} disabled={saving}
            className="ml-auto flex items-center gap-2 bg-[#E31837] hover:bg-[#C8102E] px-5 py-2.5 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-200 disabled:opacity-60"
            data-testid="editpicks-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Picks
          </button>
        )}
      </div>

      {!userId ? (
        <div className="bg-[#141414] border border-white/10 px-6 py-16 text-center text-white/50">Choose a player to view and edit their picks.</div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-white/50 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading picks…</div>
      ) : !week ? null : players.length === 0 ? (
        <div className="bg-[#141414] border border-white/10 px-6 py-12 text-center text-white/50">No players in the pool yet.</div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10" data-testid="editpicks-games">
          {week.games.map((g) => {
            const pick = selections[g.id];
            const fav = favoriteTeam(g);
            const isSpread = spreadPicks.has(g.id);
            return (
              <div key={g.id} className="p-4" data-testid={`editpicks-row-${g.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] uppercase tracking-widest text-white/40 w-32 shrink-0">{SLOT_LABEL[g.slot]}</span>
                  <span className="text-xs text-[#E31837] w-10 shrink-0">{g.points}pt</span>
                  {g.winner && <span className="text-[10px] uppercase tracking-widest text-[#22C55E]">Final · {g.winner}</span>}
                  {pick && (
                    <button onClick={() => clearPick(g.id)} className="ml-auto text-[10px] uppercase tracking-widest text-white/40 hover:text-[#EF4444] transition-colors duration-200" data-testid={`editpicks-clear-${g.id}`}>
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  {[g.away, g.home].map((t) => {
                    const line = showSpreadUI ? teamSpread(g, t) : null;
                    const selected = pick === t;
                    return (
                      <button
                        key={t}
                        onClick={() => setPick(g.id, t)}
                        className={`flex-1 flex items-center gap-2 px-3 py-2.5 border transition-colors duration-200 ${
                          selected ? "border-[#E31837] bg-[#E31837]/10" : "border-white/10 hover:border-white/30 bg-[#0A0A0A]"
                        }`}
                        data-testid={`editpicks-team-${g.id}-${t}`}
                      >
                        <span className="w-2 h-6" style={{ backgroundColor: teamColor(t) }} />
                        <div className="text-left">
                          <div className="text-base font-['Bebas_Neue'] tracking-wide leading-none">{t}</div>
                          <div className="text-[10px] text-white/50">{teamName(t)}</div>
                        </div>
                        {line != null && <span className="ml-auto text-[11px] font-bold text-white/60 tabular-nums">{line}</span>}
                        {selected && <CheckCircle2 className="w-4 h-4 text-[#E31837]" />}
                      </button>
                    );
                  })}
                </div>
                {mode === "combo" && pick && fav === pick && (
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => toggleSpread(g.id, false)}
                      className={`flex-1 py-1.5 text-[11px] uppercase tracking-wider font-semibold border transition-colors duration-200 ${!isSpread ? "border-[#E31837] bg-[#E31837]/10 text-white" : "border-white/10 text-white/50 hover:border-white/30"}`}
                      data-testid={`editpicks-su-${g.id}`}
                    >
                      Straight Up
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleSpread(g.id, true)}
                      className={`flex-1 py-1.5 text-[11px] uppercase tracking-wider font-semibold border transition-colors duration-200 ${isSpread ? "border-[#EAB308] bg-[#EAB308]/10 text-[#EAB308]" : "border-white/10 text-white/50 hover:border-white/30"}`}
                      data-testid={`editpicks-ats-${g.id}`}
                    >
                      With Spread ({teamSpread(g, pick)})
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScoringRules() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payouts = (form.payouts || [])
        .map((p) => parseFloat(p))
        .filter((n) => !isNaN(n) && n >= 0);
      await api.put("/admin/settings", {
        wrong_pick_penalty: parseFloat(form.wrong_pick_penalty),
        week_winner_bonus: parseFloat(form.week_winner_bonus),
        lone_wolf_bonus: parseFloat(form.lone_wolf_bonus),
        points_thu_mon: parseFloat(form.points_thu_mon),
        points_sun_early: parseFloat(form.points_sun_early),
        points_snf: parseFloat(form.points_snf),
        pick_mode: form.pick_mode || "straight_up",
        cover_bonus: parseFloat(form.cover_bonus ?? 1),
        pot_total: parseFloat(form.pot_total ?? 0) || 0,
        payouts,
      });
      toast.success("Scoring rules updated", { description: "All results recalculated." });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-white/50 py-10"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>;

  const field = (key, label, help) => (
    <div className="bg-[#141414] border border-white/10 p-5">
      <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-2">{label}</label>
      <input type="number" step="0.5" value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="bg-[#0A0A0A] border border-white/10 px-4 py-3 rounded-sm w-full text-2xl font-['Bebas_Neue'] tracking-wide focus:ring-2 focus:ring-[#E31837] focus:outline-none"
        data-testid={`setting-${key}`} />
      <p className="text-xs text-white/40 mt-2">{help}</p>
    </div>
  );

  return (
    <div className="max-w-4xl">
      <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide mb-1">PICK MODE</h3>
      <p className="text-xs text-white/40 mb-3">Choose how picks are graded league-wide. Switching recalculates every week instantly.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {[
          ["straight_up", "Straight Up", "Pick the outright winner of each game."],
          ["ats", "Against the Spread", "Pick the team that covers the point spread. Spreads auto-pull from ESPN (editable per game) and lock at cutoff; a push voids that game."],
          ["combo", "Straight Up + Spread", "One pick per game. Anyone can be picked straight up; the favorite can also be taken WITH the spread — cover for slot points + a cover bonus, fail to cover and it's a wrong pick (push = graded straight up)."],
        ].map(([val, label, help]) => (
          <button
            key={val}
            onClick={() => setForm({ ...form, pick_mode: val })}
            className={`text-left p-5 border transition-colors duration-200 ${form.pick_mode === val ? "border-[#E31837] bg-[#E31837]/10" : "border-white/10 hover:border-white/30 bg-[#141414]"}`}
            data-testid={`pick-mode-${val}`}
          >
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full border-2 ${form.pick_mode === val ? "bg-[#E31837] border-[#E31837]" : "border-white/40"}`} />
              <span className="font-['Bebas_Neue'] text-2xl tracking-wide leading-none">{label}</span>
            </div>
            <p className="text-xs text-white/40 mt-2">{help}</p>
          </button>
        ))}
      </div>

      {form.pick_mode === "combo" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="combo-settings">
          {field("cover_bonus", "Cover Bonus", "Extra points when a spread pick on the favorite covers (on top of the slot points).")}
        </div>
      )}
      {form.pick_mode !== "combo" && <div className="mb-4" />}

      <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide mb-1">GAME POINT VALUES</h3>
      <p className="text-xs text-white/40 mb-3">Set how many points a correct pick is worth by game slot. Saving updates every game in the season.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {field("points_thu_mon", "Thursday / Monday", "Standalone weeknight & Monday games.")}
        {field("points_sun_early", "Sunday Games", "All Sunday afternoon (1pm / 4pm) games.")}
        {field("points_snf", "Sunday Night", "The Sunday Night Football game.")}
      </div>

      <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide mb-1">BONUSES & PENALTY</h3>
      <p className="text-xs text-white/40 mb-3">Extra points and deductions applied to weekly scores.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        {field("wrong_pick_penalty", "Wrong Pick Penalty", "Points subtracted for each incorrect pick (a half-point loss = 0.5).")}
        {field("lone_wolf_bonus", "Lone Wolf Bonus", "Bonus when you're the ONLY player to correctly pick a game.")}
        {field("week_winner_bonus", "Week Winner Bonus", "Bonus for the sole highest scorer each week (none on a tie).")}
      </div>

      <PotAndPayouts form={form} setForm={setForm} />

      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 bg-[#E31837] hover:bg-[#C8102E] px-6 py-3 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-200 disabled:opacity-60"
        data-testid="save-settings-button">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sliders className="w-4 h-4" />} Save Scoring Rules
      </button>
    </div>
  );
}

function PotAndPayouts({ form, setForm }) {
  const payouts = form.payouts || [];
  const pot = parseFloat(form.pot_total ?? 0) || 0;
  const totalPct = payouts.reduce((s, p) => s + (parseFloat(p) || 0), 0);
  const pctOff = Math.abs(totalPct - 100) > 0.001;

  const setPayout = (i, val) => {
    const next = payouts.slice();
    next[i] = val;
    setForm({ ...form, payouts: next });
  };
  const addPos = () => setForm({ ...form, payouts: [...payouts, 0] });
  const removePos = (i) => setForm({ ...form, payouts: payouts.filter((_, idx) => idx !== i) });
  const evenSplit = () => {
    if (payouts.length === 0) return;
    const each = Math.round((100 / payouts.length) * 100) / 100;
    const arr = payouts.map(() => each);
    // absorb rounding into position 1
    const drift = 100 - arr.reduce((s, x) => s + x, 0);
    arr[0] = Math.round((arr[0] + drift) * 100) / 100;
    setForm({ ...form, payouts: arr });
  };

  const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="mb-6" data-testid="pot-payouts-section">
      <h3 className="font-['Bebas_Neue'] text-2xl tracking-wide mb-1">SEASON POT & PAYOUTS</h3>
      <p className="text-xs text-white/40 mb-3">Set the total pot and how it's split across finishing positions. Percentages should add up to 100%.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="bg-[#141414] border border-white/10 p-5 sm:col-span-1">
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-2">Total Pot ($)</label>
          <div className="flex items-center gap-2">
            <span className="font-['Bebas_Neue'] text-3xl text-[#EAB308] leading-none">$</span>
            <input
              type="number"
              step="1"
              min="0"
              value={form.pot_total ?? 0}
              onChange={(e) => setForm({ ...form, pot_total: e.target.value })}
              className="bg-[#0A0A0A] border border-white/10 px-4 py-3 rounded-sm w-full text-2xl font-['Bebas_Neue'] tracking-wide focus:ring-2 focus:ring-[#E31837] focus:outline-none"
              data-testid="setting-pot_total"
            />
          </div>
          <p className="text-xs text-white/40 mt-2">The total prize pool for the season.</p>
        </div>

        <div className="bg-[#141414] border border-white/10 p-5 sm:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/50">Payout Structure</span>
            <div className="flex gap-2">
              <button type="button" onClick={evenSplit} className="text-[10px] uppercase tracking-widest border border-white/20 hover:border-white/40 px-2.5 py-1.5 transition-colors duration-200" data-testid="payouts-even-split">
                Even Split
              </button>
              <button type="button" onClick={addPos} className="text-[10px] uppercase tracking-widest border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10 px-2.5 py-1.5 transition-colors duration-200 flex items-center gap-1" data-testid="payouts-add-position">
                <Plus className="w-3 h-3" /> Add Position
              </button>
            </div>
          </div>

          {payouts.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center border border-dashed border-white/10">
              No paid positions yet. Add one to build the payout structure.
            </div>
          ) : (
            <div className="space-y-2">
              {payouts.map((p, i) => {
                const pct = parseFloat(p) || 0;
                const amount = pot * (pct / 100);
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`payout-row-${i}`}>
                    <span className="font-['Bebas_Neue'] text-2xl tracking-wide text-[#EAB308] w-14 shrink-0 leading-none">{ordinal(i + 1)}</span>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={p}
                      onChange={(e) => setPayout(i, e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 px-3 py-2 rounded-sm w-24 text-lg font-['Bebas_Neue'] tracking-wide text-center focus:ring-2 focus:ring-[#E31837] focus:outline-none"
                      data-testid={`payout-pct-${i}`}
                    />
                    <span className="text-white/40 text-sm">%</span>
                    <span className="text-white/30 text-sm ml-auto">=</span>
                    <span className="font-['Bebas_Neue'] text-xl tracking-wide text-white tabular-nums w-24 text-right" data-testid={`payout-amount-${i}`}>
                      {money(amount)}
                    </span>
                    <button type="button" onClick={() => removePos(i)} className="text-white/40 hover:text-[#EF4444] transition-colors duration-200 p-1.5" data-testid={`payout-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between text-xs border-t border-white/10 pt-3">
            <span className="text-white/50">Total percentage</span>
            <span className={`font-['Bebas_Neue'] text-xl tracking-wide ${pctOff ? "text-[#EF4444]" : "text-[#22C55E]"}`} data-testid="payouts-total-pct">
              {totalPct.toFixed(2)}%
            </span>
          </div>
          {pctOff && (
            <p className="text-[11px] text-[#EF4444]/80 mt-1">Payout percentages don't add up to 100%. Adjust so they total exactly 100.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Players() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uname, setUname] = useState("");
  const [pass, setPass] = useState("");
  const [adding, setAdding] = useState(false);
  const [invite, setInvite] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then((res) => setUsers(res.data)).finally(() => setLoading(false));
  };
  const loadInvite = () => api.get("/admin/invite").then((res) => setInvite(res.data.code)).catch(() => {});
  useEffect(() => { load(); loadInvite(); }, []);

  const inviteUrl = invite ? `${window.location.origin}/join/${invite}` : "";

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied!");
    } catch {
      toast.message("Copy this link", { description: inviteUrl });
    }
  };

  const regenInvite = async () => {
    if (!window.confirm("Generate a new invite link? The old link will stop working.")) return;
    try {
      const res = await api.post("/admin/invite/regenerate");
      setInvite(res.data.code);
      toast.success("New invite link generated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.username}:`);
    if (!pw) return;
    try {
      await api.post(`/admin/users/${u.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${u.username}`, { description: `New password: ${pw}` });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const rename = async (u) => {
    const name = window.prompt(`New username for ${u.username}:`, u.username);
    if (!name || name.trim() === u.username) return;
    try {
      const res = await api.put(`/admin/users/${u.id}/username`, { username: name });
      toast.success(`Renamed ${u.username} → ${res.data.username}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/users", { username: uname, password: pass });
      toast.success(`Added ${uname}`);
      setUname(""); setPass("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.username} from the pool? Their picks will be deleted.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Removed ${u.username}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="bg-[#141414] border border-white/10 p-4 mb-6" data-testid="invite-card">
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="w-4 h-4 text-[#E31837]" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-white/50">Pool Invite Link</span>
        </div>
        <p className="text-xs text-white/40 mb-3">Share this link so new members can join and register in one tap.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input readOnly value={inviteUrl} onFocus={(e) => e.target.select()}
            className="flex-1 min-w-[240px] bg-[#0A0A0A] border border-white/10 px-3 py-2.5 rounded-sm text-sm text-white/80 focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid="invite-url" />
          <button onClick={copyInvite} className="flex items-center gap-2 bg-[#E31837] hover:bg-[#C8102E] px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest font-semibold transition-colors duration-200" data-testid="copy-invite-button">
            <Copy className="w-4 h-4" /> Copy
          </button>
          <button onClick={regenInvite} className="flex items-center gap-2 border border-white/20 hover:border-white/40 px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest transition-colors duration-200" data-testid="regenerate-invite-button">
            <RefreshCw className="w-4 h-4" /> New Link
          </button>
        </div>
      </div>

      <form onSubmit={add} className="flex flex-wrap items-end gap-3 mb-6 bg-[#141414] border border-white/10 p-4">
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-1">New Player</label>
          <input value={uname} onChange={(e) => setUname(e.target.value)} placeholder="username"
            className="bg-[#0A0A0A] border border-white/10 px-4 py-2.5 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid="add-player-username" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-1">Temp Password</label>
          <input value={pass} onChange={(e) => setPass(e.target.value)} placeholder="password"
            className="bg-[#0A0A0A] border border-white/10 px-4 py-2.5 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none" data-testid="add-player-password" />
        </div>
        <button type="submit" disabled={adding} className="flex items-center gap-2 bg-[#E31837] hover:bg-[#C8102E] px-5 py-2.5 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-200 disabled:opacity-60" data-testid="add-player-button">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add
        </button>
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading players…</div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          <div className="flex items-center px-4 py-2 bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-white/40">
            <span className="flex-1">Player</span>
            <span className="w-24">Weeks Played</span>
            <span className="w-52 text-right">Actions</span>
          </div>
          {users.map((u) => (
            <div key={u.id} className="flex items-center px-4 py-3" data-testid={`player-row-${u.username}`}>
              <span className="flex-1 flex items-center gap-2">
                <Users className="w-4 h-4 text-white/30" />
                {u.username}
                {u.role === "admin" && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-[#E31837] px-1.5 py-0.5 uppercase tracking-wider">
                    <ShieldAlert className="w-3 h-3" /> Admin
                  </span>
                )}
              </span>
              <span className="w-24 text-white/60 text-sm">{u.weeks_played}</span>
              <span className="w-52 text-right flex items-center justify-end gap-3">
                {u.role === "admin" ? (
                  <span className="text-white/20 text-xs">—</span>
                ) : (
                  <>
                    <button onClick={() => rename(u)} className="flex items-center gap-1 text-white/50 hover:text-white transition-colors duration-200 text-xs uppercase tracking-wider" data-testid={`rename-${u.username}`}>
                      <Pencil className="w-4 h-4" /> Rename
                    </button>
                    <button onClick={() => resetPw(u)} className="flex items-center gap-1 text-white/50 hover:text-[#EAB308] transition-colors duration-200 text-xs uppercase tracking-wider" data-testid={`reset-pw-${u.username}`}>
                      <KeyRound className="w-4 h-4" /> Reset
                    </button>
                    <button onClick={() => remove(u)} className="text-white/40 hover:text-[#EF4444] transition-colors duration-200" data-testid={`delete-player-${u.username}`}>
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
